/**
 * ScansView — full functionality matching the screenshots:
 *  - Header: target icon + "Scans" title
 *  - Filter (n) bar with Save Query button
 *  - Active / Saved tabs
 *  - Scan In: All Stocks | Watchlist | Sector dropdown
 *  - Add a Filter button → opens modal listing all 59 filters by category
 *  - Each active filter renders an editable row (operator + value)
 *  - Scan button runs the active filter set against the chosen universe
 *  - Save Query persists to localStorage (and lists in Saved tab)
 *  - Results table with sortable columns + filter values + Trade button
 */
import { useEffect, useMemo, useState } from 'react';
import {
  FILTER_REGISTRY, FILTER_BY_ID, runScan, localScanStore,
  type ActiveFilter, type FilterDef, type SavedQuery, type ScanUniverse, type Operator, type SymbolRow,
} from '../lib/scan-filters';

interface Props {
  universe: SymbolRow[];
  watchlistSymbols: string[];
  onSelectSymbol?: (s: string) => void;
  onJumpToTrade?: () => void;
}

const OPERATORS: Operator[] = ['>', '>=', '<', '<=', '=', '!='];

export function ScansView({ universe, watchlistSymbols, onSelectSymbol, onJumpToTrade }: Props) {
  const [tab, setTab] = useState<'Active' | 'Saved'>('Active');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [scanIn, setScanIn] = useState<ScanUniverse>('All Stocks');
  const [showAddModal, setShowAddModal] = useState(false);
  const [savedNames, setSavedNames] = useState<string[]>([]);
  const [results, setResults] = useState<ReturnType<typeof runScan>>([]);
  const [scanned, setScanned] = useState(false);

  useEffect(() => { void localScanStore.list().then(setSavedNames); }, []);

  const effectiveUniverse = useMemo(() => {
    if (scanIn === 'Watchlist') return universe.filter((u) => watchlistSymbols.includes(u.symbol));
    return universe; // 'Sector' filter would join here when sector data lands.
  }, [scanIn, universe, watchlistSymbols]);

  function addFilter(id: string) {
    const def = FILTER_BY_ID[id]; if (!def) return;
    if (filters.some((f) => f.id === id)) return; // unique
    setFilters((prev) => [...prev, { id, op: '>', value: defaultValue(def) }]);
    setShowAddModal(false);
  }
  function removeFilter(id: string) { setFilters((prev) => prev.filter((f) => f.id !== id)); }
  function updateFilter(id: string, patch: Partial<ActiveFilter>) {
    setFilters((prev) => prev.map((f) => f.id === id ? { ...f, ...patch } : f));
  }

  function scan() {
    setResults(runScan(effectiveUniverse, filters));
    setScanned(true);
  }

  async function saveQuery() {
    const name = (typeof prompt !== 'undefined') ? prompt('Save query as:') : null;
    if (!name) return;
    await localScanStore.save({ name, filters, scanIn, savedAt: new Date().toISOString() });
    setSavedNames(await localScanStore.list());
  }
  async function loadSaved(name: string) {
    const q = await localScanStore.load(name);
    if (!q) return;
    setFilters(q.filters); setScanIn(q.scanIn); setTab('Active');
  }
  async function deleteSaved(name: string) {
    if (typeof confirm !== 'undefined' && !confirm(`Delete "${name}"?`)) return;
    await localScanStore.remove(name);
    setSavedNames(await localScanStore.list());
  }

  return (
    <div className="sv">
      <div className="sv-head">
        <h2><span aria-hidden>◎</span> Scans</h2>
      </div>

      <div className="sv-filterbar">
        <span className="sv-fcount">Filter <span className="badge">{filters.length}</span></span>
        <button className="sv-savebtn" onClick={() => void saveQuery()} disabled={filters.length === 0}>Save Query</button>
      </div>

      <div className="sv-tabs">
        {(['Active','Saved'] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t}
                  className={`sv-tab ${tab === t ? 'active' : ''}`}
                  onClick={() => setTab(t)}>{t} {t === 'Saved' ? `(${savedNames.length})` : ''}</button>
        ))}
      </div>

      {tab === 'Active' ? (
        <>
          <div className="sv-scanin">
            Scan In: <select value={scanIn} onChange={(e) => setScanIn(e.target.value as ScanUniverse)}>
              <option>All Stocks</option>
              <option>Watchlist</option>
              <option>Sector</option>
            </select>
          </div>

          <div className="sv-active">
            {filters.map((f) => {
              const def = FILTER_BY_ID[f.id];
              if (!def) return null;
              return (
                <div key={f.id} className="sv-active-row">
                  <span className="sv-active-name">{def.label}</span>
                  <select value={f.op} onChange={(e) => updateFilter(f.id, { op: e.target.value as Operator })}>
                    {OPERATORS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <input type="number" value={f.value} step="any"
                         onChange={(e) => updateFilter(f.id, { value: Number(e.target.value) })} />
                  <span className="muted">{unitLabel(def.unit)}</span>
                  <button className="sv-x" onClick={() => removeFilter(f.id)} aria-label="Remove">×</button>
                </div>
              );
            })}
          </div>

          <div className="sv-actions">
            <button className="sv-add" onClick={() => setShowAddModal(true)}>Add a Filter</button>
            <button className="sv-scan" disabled={filters.length === 0} onClick={scan}>Scan</button>
          </div>
        </>
      ) : (
        <div className="sv-saved">
          {savedNames.length === 0 ? (
            <div className="muted">No saved queries.</div>
          ) : savedNames.map((n) => (
            <div key={n} className="sv-saved-row">
              <span className="sv-saved-name">{n}</span>
              <button className="sv-link" onClick={() => void loadSaved(n)}>Load</button>
              <button className="sv-link danger" onClick={() => void deleteSaved(n)}>Delete</button>
            </div>
          ))}
        </div>
      )}

      <div className="sv-resultsh">{results.length} Results {scanned ? `(scanned ${effectiveUniverse.length} symbols)` : ''}</div>
      {results.length > 0 && (
        <div className="sv-results">
          <table className="tv-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th style={{ textAlign: 'right' }}>Last</th>
                <th style={{ textAlign: 'right' }}>%Chg</th>
                {filters.map((f) => (
                  <th key={f.id} style={{ textAlign: 'right' }}>{FILTER_BY_ID[f.id]?.label ?? f.id}</th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {results.map(({ row, values }) => (
                <tr key={row.symbol}>
                  <td><button className="sv-link" onClick={() => onSelectSymbol?.(row.symbol)}>{row.symbol}</button></td>
                  <td className="mono" style={{ textAlign: 'right' }}>{row.mark.toFixed(2)}</td>
                  <td className={`mono ${row.pctChange >= 0 ? 'up' : 'down'}`} style={{ textAlign: 'right' }}>
                    {(row.pctChange >= 0 ? '+' : '') + row.pctChange.toFixed(2)}%
                  </td>
                  {filters.map((f) => (
                    <td key={f.id} className="mono" style={{ textAlign: 'right' }}>{formatValue(values[f.id] ?? 0, FILTER_BY_ID[f.id]?.unit ?? 'plain')}</td>
                  ))}
                  <td style={{ textAlign: 'right' }}>
                    <button className="sv-link" onClick={() => { onSelectSymbol?.(row.symbol); onJumpToTrade?.(); }}>Trade</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddFilterModal
          activeIds={new Set(filters.map((f) => f.id))}
          onPick={addFilter}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

function AddFilterModal({ activeIds, onPick, onClose }: {
  activeIds: Set<string>; onPick: (id: string) => void; onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const stock = FILTER_REGISTRY.filter((f) => f.category === 'Stock' && f.label.toLowerCase().includes(q.toLowerCase()));
  const fund  = FILTER_REGISTRY.filter((f) => f.category === 'Fundamental' && f.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="sv-modal" role="dialog" aria-modal="true" aria-label="Add Scan Filters" onClick={onClose}>
      <div className="sv-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="sv-modal-h">
          <strong>Add Scan Filters</strong>
          <button className="ch-tool-cancel" onClick={onClose}>×</button>
        </div>
        <input className="sv-modal-search" placeholder="Search filters…" value={q} onChange={(e) => setQ(e.target.value)} />
        <h4 className="sv-modal-section">Stock Filters</h4>
        <div className="sv-modal-grid">
          {stock.map((f) => <FilterChip key={f.id} f={f} active={activeIds.has(f.id)} onPick={onPick} />)}
        </div>
        <h4 className="sv-modal-section">Fundamental Filters</h4>
        <div className="sv-modal-grid">
          {fund.map((f) => <FilterChip key={f.id} f={f} active={activeIds.has(f.id)} onPick={onPick} />)}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ f, active, onPick }: { f: FilterDef; active: boolean; onPick: (id: string) => void }) {
  return (
    <button className={`sv-chip ${active ? 'active' : ''}`} disabled={active} onClick={() => onPick(f.id)}
            title={f.label + ' · ' + unitLabel(f.unit)}>
      <span className="plus" aria-hidden>{active ? '✓' : '+'}</span>
      <span className="lbl">{f.label}</span>
      <span className="info" aria-hidden>ⓘ</span>
    </button>
  );
}

function unitLabel(u: string): string {
  switch (u) {
    case 'price':    return '₦';
    case 'pct':      return '%';
    case 'count':    return 'count';
    case 'currency': return '₦Bn';
    case 'ratio':    return '×';
    default:         return '';
  }
}
function formatValue(v: number, u: string): string {
  switch (u) {
    case 'price': case 'pct': case 'ratio': case 'currency':
      return v.toFixed(2);
    case 'count': return Math.round(v).toLocaleString('en-NG');
    default: return v.toFixed(2);
  }
}
function defaultValue(def: FilterDef): number {
  switch (def.unit) {
    case 'price': return 100;
    case 'pct':   return 5;
    case 'ratio': return 1;
    case 'count': return 1000;
    case 'currency': return 100;
    default: return 0;
  }
}
